import { CommonModule } from '@angular/common';
import { Component, computed, OnInit, Signal } from '@angular/core';
import { IonRouterOutlet, ModalController, IonicModule } from '@ionic/angular';
import { DesktopHeaderComponent } from 'src/app/general-components/desktop-header/desktop-header.component';
import { LoadscreenComponent } from 'src/app/general-components/loadscreen/loadscreen.component';
import { OrderSummaryComponent } from 'src/app/general-components/order-summary/order-summary.component';
import { iUser } from 'src/app/interfaces/user';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { OrderService } from 'src/app/services/order.service';
import { isPlatform } from '@ionic/angular';
import { ToastService } from 'src/app/services/toast.service';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Status } from 'src/app/interfaces/status';

@Component({
  selector: 'app-orders',
  templateUrl: './orders.page.html',
  styleUrls: ['./orders.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    DesktopHeaderComponent,
    LoadscreenComponent,
    ReactiveFormsModule
  ]
})
export class OrdersPage implements OnInit {

  public user: Signal<iUser> = computed(() => this.authService.user());

  public orders = [];
  public filteredOrders = [];
  public isLoading: boolean = true;

  public currentOrderStatuses = [];
  public orderStatuses: Status[] = [
    { status: 'new', name: 'Ny' },
    { status: 'confirmed', name: 'Bekræftet' },
    { status: 'cancelled', name: 'Aflyst' },
    { status: 'completed', name: 'Gennemført' },
    { status: 'pending', name: 'Afventer betaling' },
    { status: 'processing', name: 'Behandler' },
    { status: 'on-hold', name: 'Afventer' },
    { status: 'completed', name: 'Fuldført' },
    { status: 'cancelled', name: 'Annulleret' },
    { status: 'refunded', name: 'Refunderet' },
    { status: 'failed', name: 'Mislykket' }
  ];

  public searchForm = new FormGroup({
    text: new FormControl(''),
    status: new FormControl('')
  });


  constructor(
    public routerOutlet: IonRouterOutlet,
    private authService: AuthenticationService,
    private orderService: OrderService,
    private modalController: ModalController,
    private toastService: ToastService
  ) { }


  ngOnInit() {
    this.getOrders();

    this.searchForm.valueChanges.subscribe({
      next: (value) => {
        this.filteredOrders = this.orders.filter(order => {
          const matchesStatus = value.status ? order.order_status === value.status : true;
          const matchesText = value.text && value.text.length > 2 ? this.orderService.searchOrder(order, value.text.toLowerCase()) : true;
          return matchesStatus && matchesText;
        });
      },
      error: (error) => {
        console.error('Error in search form value changes:', error);
      }
    });
  }


  /**
   * Fetches the list of orders from the order service and updates the component state.
   * 
   * This method subscribes to the `getOrders` observable from the `orderService` and handles
   * the response by updating the `orders`, `filteredOrders`, and `currentOrderStatuses` properties.
   * It also sets the `isLoading` flag to `false` once the data is loaded or if an error occurs.
   * 
   * In case of an error, a toast message is presented to the user with the error message.
   * 
   * @returns {void}
   */
  public getOrders() {
    this.orderService.getOrders().subscribe({
      next: (data: any) => {
        this.orders = data.results.sort((a, b) => b.order_number.localeCompare(a.order_number));
        this.filteredOrders = [...this.orders];
        this.currentOrderStatuses = this.orderService.getOrderStatuses(this.orders);
        this.isLoading = false;
      },
      error: (error: any) => {
        this.toastService.presentToast('Error loading orders! - ' + error.message, 'danger')
        this.isLoading = false
      }
    });
  }


  /**
   * Displays the details of a specific order in a modal.
   * 
   * @param orderId - The ID of the order to display.
   * @returns A promise that resolves when the modal is presented.
   */
  public async showDetails(orderId: number) {

    const order = this.orders.find(o => o.order_id === orderId);
    if (!order) {
      return;
    }

    const modal = await this.modalController.create({
      component: OrderSummaryComponent,
      componentProps: {
        title: 'Ordre ' + order.order_number,
        order: order,
        showControls: true
      },
      canDismiss: true,
      presentingElement: this.routerOutlet.nativeEl,
      cssClass: isPlatform('desktop') ? 'modal-large' : ''
    });

    return await modal.present();
  }

}
